import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { useActiveChat } from "~/entities/chat/useActiveChat";
import { useProjectById } from "~/entities/project";
import { useActiveWorkspace } from "~/entities/workspace";
import { useChatSettingsPersist } from "~/features/chat-settings-persist";
import { useActiveProjectId, useActiveProjectStore } from "~/shared/store/useActiveProjectStore";

import { useAutoOpenSettingsOnDesktop } from "./useAutoOpenSettingsOnDesktop";

// Side-effects активного чата (без рендер-выхода): персист настроек,
// auto-open панели на десктопе и navigation-гарды — 404/403 redirect,
// синхронизация активного проекта workspace со сценой чата, сброс активного
// проекта при ошибке его загрузки. Все query/selector-хуки дедуплицируются
// react-query/zustand, поэтому повторное чтение тех же источников, что в
// useChatShellContext, бесплатно — зато каждый хук single-concern.
export function useChatRouteGuards(chatId: string | undefined): void {
    const navigate = useNavigate();
    const chatQuery = useActiveChat(chatId);
    const { activeWorkspaceId } = useActiveWorkspace();
    const workspaceId = activeWorkspaceId ?? undefined;
    const activeProjectId = useActiveProjectId(workspaceId);
    const setActiveProject = useActiveProjectStore((s) => s.setActive);
    const projectQuery = useProjectById(activeProjectId ?? undefined);
    const chatProjectId = chatQuery.data?.projectId ?? null;

    useChatSettingsPersist(chatId ?? null);
    useAutoOpenSettingsOnDesktop();

    // Guard: если чат не принадлежит активному workspace или вернулся
    // ошибкой (404 / 403) — уходим на empty state.
    useEffect(() => {
        if (!chatId) return;
        if (chatQuery.isError) {
            void navigate("/chat", { replace: true });
            return;
        }
        if (chatQuery.data && activeWorkspaceId && chatQuery.data.workspaceId !== activeWorkspaceId) {
            void navigate("/chat", { replace: true });
        }
    }, [chatId, chatQuery.data, chatQuery.isError, activeWorkspaceId, navigate]);

    useEffect(() => {
        if (!workspaceId) return;
        if (chatProjectId && chatProjectId !== activeProjectId) {
            setActiveProject(workspaceId, chatProjectId);
        }
    }, [chatProjectId, workspaceId, activeProjectId, setActiveProject]);

    useEffect(() => {
        if (!workspaceId || !activeProjectId) return;
        if (projectQuery.isError) {
            setActiveProject(workspaceId, null);
        }
    }, [projectQuery.isError, workspaceId, activeProjectId, setActiveProject]);
}
